<script>
import {ref, onMounted, computed} from 'vue';
import axios from 'axios';
import config from "@/utils/config";
import {
  Chart,
  LineController,
  LineElement,
  PointElement,
  LinearScale,
  CategoryScale,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';

Chart.register(LineController, LineElement, PointElement, LinearScale, CategoryScale, Title, Tooltip, Legend);

export default {
  name: 'UserProfile',
  props: {
    userId: {
      type: Number,
      required: true
    },
    isOwnProfile: {
      type: Boolean,
      required: true
    },
    student: {
      type: Boolean,
      required: true
    }
  },
  data() {
    return {
      is_Single_selected: true,
      is_Group_selected: false,
      about_single: false,
      about_group: false,
      chartInstance: null,
      // New state for role management
      showRoleModal: false,
      selectedRole: null,
      isBuddy: null,
      roleChangeSuccess: false,
      roleChangeError: false
    }
  },
  setup(props) {
    const userData = ref({});
    const teachingSubjects = ref([]);
    const declaredMarks = ref([]);
    const neededSubjects = ref([]);
    const userPosition = ref("");
    const averageRatings = ref({
      overall: 0,
      kindness: 0,
      punctuality: 0,
      competenceOrPerformance: 0,
      commitment: 0
    });
    const anonymizedComments = ref([]);
    const badges = ref([]);
    const missingBadges = ref([]);

    const isOwnProfile = props.isOwnProfile;
    const hasEnoughEvaluations = ref(false);

    const hasAnyRatings = computed(() => {
      return Object.values(averageRatings.value).some(rating => rating > 0);
    });

    const getStarRating = (rating) => {
      const fullStars = Math.floor(rating);
      const halfStar = rating % 1 >= 0.5;
      const emptyStars = 5 - Math.ceil(rating);

      return '★'.repeat(fullStars) +
          (halfStar ? '½' : '') +
          '☆'.repeat(emptyStars);
    };

    const fetchUserData = async () => {
      try {
        // Prima richiesta per ottenere i dati utente base
        let response = await axios.get(`${config.baseUrl}/user/${props.userId}`);
        userData.value = response.data;

        // Seconda richiesta per ottenere la posizione
        const role = props.student ? 'student' : 'tutor';
        response = await axios.get(`${config.baseUrl}/user/position/${role}/${props.userId}`);

        // Qui assegniamo la posizione alla ref separata
        if (response.data === 0 || response.data === undefined || response.data === null || response.data === "") {
          userPosition.value = "Non sei in classifica";
        } else {
          userPosition.value = `In classifica sei il numero: ${response.data}`;
        }

        console.log("userData:", userData.value);
        console.log("userPosition:", userPosition.value);
      } catch (error) {
        console.error('Error fetching user data:', error);
      }
    };

    const fetchEvaluations = async () => {
      try {
        const response = await axios.get(`${config.baseUrl}/valutazione/aggregates/${props.userId}`);
        const data = response.data;
        if (data.totalEvaluations >= 2) {
          hasEnoughEvaluations.value = true;
          averageRatings.value = data.averages;
          anonymizedComments.value = data.comments.filter(comment => comment.length > 0);
        }
      } catch (error) {
        console.error('Error fetching evaluations:', error);
      }
    };

    const fetchAdditionalData = async () => {
      if (!isOwnProfile.value) {
        try {
          const [teachingResp, marksResp, neededResp] = await Promise.all([
            axios.get(`${config.baseUrl}/utenteInsegnaMateria/byUser/${props.userId}`),
            axios.get(`${config.baseUrl}/voto-dichiarato/utente/${props.userId}`),
            axios.get(`${config.baseUrl}/utenteRecuperaMateria/utente/${props.userId}`)
          ]);

          teachingSubjects.value = teachingResp.data;
          declaredMarks.value = marksResp.data;
          neededSubjects.value = neededResp.data;
        } catch (error) {
          console.error('Error fetching additional data:', error);
        }
      }
    };

    const fetchBadges = async () => {
      try {
        const response = await axios.get(`${config.baseUrl}/utenteOttieneBadge/utente/${props.userId}`);
        console.log("Badges response:", response.data);
        badges.value = response.data;
      } catch (error) {
        console.error('Error fetching badges:', error);
      }

      try{
        const response = await axios.get(`${config.baseUrl}/utenteOttieneBadge/missingBadges/${props.userId}`);
        missingBadges.value = response.data;
      } catch (error) {
        console.error('Error fetching blocked badges:', error);
      }
    };

    const getBadgeIcon = (type) => {
      const icons = {
        'Insegnamento': '👨‍🏫',
        'Apprendimento': '📚',
        'Sociale': '🤝',
        'Achievement': '🏆'
      };
      return icons[type] || '🎯';
    };

    const getBadgeColorClass = (type) => {
      return `badge-${type.toLowerCase()}`;
    };

    onMounted(async () => {
      await fetchUserData();
      await fetchEvaluations();
      await fetchAdditionalData();
      await fetchBadges()
    });

    return {
      userData,
      teachingSubjects,
      userPosition,
      declaredMarks,
      neededSubjects,
      averageRatings,
      anonymizedComments,
      hasEnoughEvaluations,
      hasAnyRatings,
      getStarRating,
      badges,
      missingBadges,
      getBadgeIcon,
      getBadgeColorClass,
    };
  },
  methods: {
    openRoleModal() {
      this.showRoleModal = true;
      this.selectedRole = this.student ? 'student' : 'tutor';
      this.isBuddy = null;
    },

    closeRoleModal() {
      this.showRoleModal = false;
      this.roleChangeSuccess = false;
      this.roleChangeError = false;
    },

    selectRole(role) {
      this.selectedRole = role;
      if (role === 'tutor') {
        this.isBuddy = null;
      }
    },

    setBuddy(value) {
      this.isBuddy = value;
    },

    async confirmRoleChange() {
      if (!this.canConfirmRole) return;

      try {
        const params = new URLSearchParams();
        params.append('role', this.selectedRole);
        if (this.selectedRole === 'student') {
          params.append('isBuddy', this.isBuddy.toString());
        }

        await fetch(`${config.baseUrl}/user/changeRole/${this.userId}?${params.toString()}`, {
          method: 'POST'
        });

        this.roleChangeSuccess = true;
        setTimeout(() => {
          window.location.reload();
        }, 2000);
      } catch (error) {
        console.error('Error changing role:', error);
        this.roleChangeError = true;
      }
    },

    single_selected() {
      this.is_Single_selected = true;
      this.is_Group_selected = false;

      if (this.chartInstance)
        this.chartInstance.destroy();
    },
    group_selected() {
      this.is_Group_selected = true;
      this.is_Single_selected = false;
      this.about_group = false;
      this.about_single = false;

      if (this.chartInstance)
        this.chartInstance.destroy();

      this.generate_chart();
    },
    compare_single() {
      this.about_single = true;
      this.about_group = false;

      if (this.chartInstance)
        this.chartInstance.destroy();

      this.generate_chart();
    },
    compare_group() {
      this.about_group = true;
      this.about_single = false;

      if (this.chartInstance)
        this.chartInstance.destroy();

      this.generate_chart();
    },
    async generate_chart() {
      let response;
      console.log("Generating chart with userId:", this.userId, "student:", this.student, "is_Single_selected:", this.is_Single_selected, "about_single:", this.about_single);
      try {
        if (this.student) {
          if (this.is_Single_selected && this.about_single) {
            response = await axios.get(`${config.baseUrl}/valutazione/andamento/student/${this.userId}`);     //andamento rispetto alla media di tutti gli studenti/buddy
          } else if (this.is_Single_selected) {
            response = await axios.get(`${config.baseUrl}/valutazione/andamento/group/${this.userId}`);       //andamento rispetto alla media di tutti i gruppi a cui ha partecipato
          } else {
            response = await axios.get(`${config.baseUrl}/valutazione/andamento/rispetto/group/${this.userId}`);    //andamento rispetto alla media di tutti i gruppi
          }
        } else {
          if (this.is_Single_selected && this.about_single) {
            response = await axios.get(`${config.baseUrl}/valutazione/andamento/tutor/${this.userId}`);
          } else if (this.is_Single_selected) {
            response = await axios.get(`${config.baseUrl}/valutazione/andamento/tutor/group/${this.userId}`);
          }
        }
        console.log("Response data:", response.data);
        this.paint_chart(response);
      } catch (error) {
        console.error("Error fetching chart data:", error);
      }
    },
    paint_chart(response) {
      const data = response.data;

      const averageEvaluation = data['averageEvaluation'];
      const evaluations = data['evaluations'];

      if (this.chartInstance)
        this.chartInstance.destroy();

      const labels = evaluations.map(evaluation => {
        const date = new Date(evaluation.dataOra);
        return date.toISOString().split('T')[0]; // YYYY-MM-DD
      });

      let media;
      if (this.is_Single_selected)
        media = this.about_single ? 'Media studenti' : 'Media gruppi';
      else
        media = 'Media gruppi';

      const valutazioniComplessive = evaluations.map(evaluation => evaluation['valutazioneComplessiva']);
      const valutazioniGentilezza = evaluations.map(evaluation => evaluation['valutazioneGentilezza']);
      const valutazioniPuntualita = evaluations.map(evaluation => evaluation['valutazionePuntualita']);
      const votoImpegno = evaluations.map(evaluation => evaluation['votoImpegno']);
      const valutazioneCompetenzaORendimento = evaluations.map(evaluation => evaluation['valutazioneCompetenzaORendimento']);

      const ctx = document.getElementById('myChart').getContext('2d');
      document.getElementById('myChart').style.display = 'block';
      document.getElementById('myChart').style.backgroundColor = '#f5f5f5';
      document.getElementById('myChart').style.padding = '10px';
      document.getElementById('myChart').style.borderRadius = '5px';


      this.chartInstance = new Chart(ctx, {
        type: 'line',
        data: {
          labels: labels,
          datasets: [
            {
              label: 'Valutazione Complessiva',
              data: valutazioniComplessive,
              borderColor: 'blue',
              backgroundColor: 'rgba(0, 0, 255, 0.1)',
              borderWidth: 2,
              pointStyle: 'circle',
              pointRadius: 2,
              fill: false,
              tension: 0.4,
            },
            {
              label: 'Valutazione Competenza o Rendimento',
              data: valutazioneCompetenzaORendimento,
              borderColor: 'green',
              backgroundColor: 'rgba(0, 255, 0, 0.1)',
              borderWidth: 2,
              pointStyle: 'circle',
              pointRadius: 2,
              fill: false,
              tension: 0.4,
              hidden: true,
            },
            {
              label: 'Valutazione Gentilezza',
              data: valutazioniGentilezza,
              borderColor: 'purple',
              backgroundColor: 'rgba(128, 0, 128, 0.1)',
              borderWidth: 2,
              pointStyle: 'circle',
              pointRadius: 2,
              fill: false,
              tension: 0.4,
              hidden: true,
            },
            {
              label: 'Valutazione Puntualità',
              data: valutazioniPuntualita,
              borderColor: 'orange',
              backgroundColor: 'rgba(255, 165, 0, 0.1)',
              borderWidth: 2,
              pointStyle: 'circle',
              pointRadius: 2,
              fill: false,
              tension: 0.4,
              hidden: true,
            },
            {
              label: 'Voto Impegno',
              data: votoImpegno,
              borderColor: 'teal',
              backgroundColor: 'rgba(0, 128, 128, 0.1)',
              borderWidth: 2,
              pointStyle: 'circle',
              pointRadius: 2,
              fill: false,
              tension: 0.4,
              hidden: true,
            },
            {
              label: media,
              data: Array(labels.length).fill(averageEvaluation),
              borderColor: 'red',
              borderWidth: 1,
              borderDash: [10, 5],
              pointRadius: 0,
              tension: 0,
            },
          ],
        },
        options: {
          responsive: true,
          plugins: {
            legend: {
              position: 'top',
            },
            tooltip: {
              enabled: true,
            },
          },
          scales: {
            x: {
              title: {
                display: true,
                text: 'Data',
              },
            },
            y: {
              title: {
                display: true,
                text: 'Valutazione',
              },
              suggestedMin: 0,
              suggestedMax: 5,
            },
          },
        },
      });
    },
  },
  computed: {
    canConfirmRole() {
      if (this.selectedRole === 'tutor') return true;
      if (this.selectedRole === 'student') return this.isBuddy !== null;
      return false;
    }
  }
}
</script>

<template>
  <div class="user-profile" style="caret-color: transparent">
    <!-- Role change modal -->
    <div v-if="showRoleModal" class="modal-overlay">
      <div class="modal-content">
        <h2 class="text-xl font-semibold mb-6">Modifica il tuo ruolo</h2>
        <div class="space-y-6">
          <div class="flex justify-center gap-4 mb-8">
            <button
                @click="selectRole('tutor')"
                :class="['role-btn', selectedRole === 'tutor' ? 'role-btn-active' : '']"
            >
              Tutor
            </button>
            <button
                @click="selectRole('student')"
                :class="['role-btn', selectedRole === 'student' ? 'role-btn-active' : '']"
            >
              Studente
            </button>
          </div>

          <!-- Buddy option for students -->
          <div v-if="selectedRole === 'student'" class="transition-all duration-300">
            <p class="mb-4 text-gray-700">Vuoi essere disponibile come tutor tra pari?</p>
            <div class="flex justify-center gap-4">
              <button
                  @click="setBuddy(true)"
                  :class="['buddy-btn', isBuddy === true ? 'buddy-btn-active' : '']"
              >
                Sì
              </button>
              <button
                  @click="setBuddy(false)"
                  :class="['buddy-btn', isBuddy === false ? 'buddy-btn-active' : '']"
              >
                No
              </button>
            </div>
          </div>

          <div v-if="roleChangeSuccess" class="success-message">
            Ruolo aggiornato con successo! La pagina verrà ricaricata...
          </div>

          <div v-if="roleChangeError" class="error-message">
            Si è verificato un errore durante l'aggiornamento del ruolo.
          </div>

          <div class="button-group">
            <button
                @click="confirmRoleChange"
                :disabled="!canConfirmRole"
                class="confirm-btn"
                :class="{'confirm-btn-disabled': !canConfirmRole}"
            >
              Conferma
            </button>
            <button @click="closeRoleModal" class="cancel-btn">
              Annulla
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Add role change button for own profile TODO ripristinare-->
    <!-- <div v-if="isOwnProfile" class="role-change-section">
      <button @click="openRoleModal" class="change-role-btn">
        Modifica ruolo
      </button>
    </div> -->
    <div class="basic-info" v-if="!isOwnProfile">
      <h2 class="user-name">{{ userData.nome }} {{ userData.cognome }}</h2>

      <div class="info-section" v-if="teachingSubjects.length > 0">
        <h4>📚 Materie insegnate:</h4>
        <ul class="info-list">
          <li v-for="subject in teachingSubjects" :key="subject.id">
            {{ subject.nome }}
          </li>
        </ul>
      </div>

      <div class="info-section" v-if="declaredMarks.length > 0">
        <h4>📝 Voti dichiarati:</h4>
        <ul class="info-list">
          <li v-for="mark in declaredMarks" :key="mark.id">
            {{ mark.materia.nome }}: <strong>{{ mark.voto }}</strong>
          </li>
        </ul>
      </div>

      <div class="info-section" v-if="neededSubjects.length > 0">
        <h4>🎯 Materie da recuperare:</h4>
        <ul class="info-list">
          <li v-for="subject in neededSubjects" :key="subject.id">
            {{ subject.materiaNome }}
          </li>
        </ul>
      </div>
    </div>

    <div class="position-section">
      <h3>{{ userPosition }}</h3>
    </div>

    <div class="evaluations">
      <h4>⭐ Valutazioni ricevute</h4>
      <div class="ratings" v-if="hasEnoughEvaluations">
        <div class="rating-item" v-if="averageRatings.overall > 0">
          <span class="rating-label">Media complessiva:</span>
          <div class="rating-value">
            <span class="stars">{{ getStarRating(averageRatings.overall) }}</span>
            <span class="numeric-rating">({{ averageRatings.overall.toFixed(1) }}/5)</span>
          </div>
        </div>
        <div class="rating-item" v-if="averageRatings.kindness > 0">
          <span class="rating-label">Gentilezza:</span>
          <div class="rating-value">
            <span class="stars">{{ getStarRating(averageRatings.kindness) }}</span>
            <span class="numeric-rating">({{ averageRatings.kindness.toFixed(1) }}/5)</span>
          </div>
        </div>
        <div class="rating-item" v-if="averageRatings.punctuality > 0">
          <span class="rating-label">Puntualità:</span>
          <div class="rating-value">
            <span class="stars">{{ getStarRating(averageRatings.punctuality) }}</span>
            <span class="numeric-rating">({{ averageRatings.punctuality.toFixed(1) }}/5)</span>
          </div>
        </div>
        <div class="rating-item" v-if="averageRatings.competenceOrPerformance > 0">
          <span class="rating-label">{{ isOwnProfile ? 'Competenza' : 'Rendimento' }}:</span>
          <div class="rating-value">
            <span class="stars">{{ getStarRating(averageRatings.competenceOrPerformance) }}</span>
            <span class="numeric-rating">({{ averageRatings.competenceOrPerformance.toFixed(1) }}/5)</span>
          </div>
        </div>
        <div class="rating-item" v-if="averageRatings.commitment > 0">
          <span class="rating-label">Impegno:</span>
          <div class="rating-value">
            <span class="stars">{{ getStarRating(averageRatings.commitment) }}</span>
            <span class="numeric-rating">({{ averageRatings.commitment.toFixed(1) }}/5)</span>
          </div>
        </div>
        <div class="d-flex justify-content-center">
          <div class="input-form">
            <h4>📊 Analisi andamento</h4>
            <form>
              <div class="checkbox-container" v-if="this.student">
                <div>
                  <button type="button" :class="{ active: is_Single_selected }" @click="this.single_selected">Singolo
                  </button>
                </div>
                <div>
                  <button type="button" :class="{ active: is_Group_selected }" @click="this.group_selected">Gruppo
                  </button>
                </div>
              </div>
              <div class="trend-choose">
                <div v-if="is_Single_selected">
                  <div class="checkbox-row">
                    <input type="checkbox" name="student-mean" id="student-mean" v-model="about_single"
                           @click="compare_single()">
                    <label
                        for="student-mean">{{
                        this.student ? 'Analisi rispetto alla media degli studenti' : 'Analisi rispetto alla media dei tutor'
                      }}</label>
                  </div>
                  <div class="checkbox-row">
                    <input type="checkbox" name="group-mean" id="group-mean" v-model="about_group"
                           @click="compare_group()">
                    <label for="group-mean">Analisi rispetto alla media dei gruppi</label>
                  </div>
                </div>
              </div>
            </form>
          </div>
          <canvas id="myChart" style="display: none"></canvas>
          <p id="no_data" style="display: none">No data available to generate a chart</p>
        </div>
      </div>
      <div v-else class="no-evaluations">
        📊 Non ci sono abbastanza valutazioni per mostrare una media.
      </div>
      <div class="comments" v-if="anonymizedComments.length > 0">
        <h4>💭 Commenti</h4>
        <div class="comment-box" v-for="(comment, index) in anonymizedComments" :key="index">
          <p>"{{ comment }}"</p>
        </div>
      </div>
    </div>
    <div class="badges-section">
      <h4>🏆 Badge ottenuti</h4>
      <div class="badges-container" v-if="badges.length > 0">
        <div v-for="badge in badges" :key="badge.nome" class="badge-item">
          <div class="badge-icon" :class="getBadgeColorClass(badge.tipo)">
            {{ getBadgeIcon(badge.tipo) }}
          </div>
          <div class="badge-info">
            <h4>{{ badge.visibleName }}</h4>
            <p class="badge-level">Livello: {{ badge.livello }}</p>
            <p class="badge-type">{{ badge.target}}</p>
          </div>
        </div>
      </div>
      <div v-else class="no-badges">
        Nessun badge ottenuto finora.
      </div>
    </div>
    <div class="badges-section">
      <h4>🔒 Badge bloccati</h4>
      <div class="badges-container" v-if="missingBadges.length > 0">
        <div v-for="missbadge in missingBadges" :key="missbadge.nome" class="missingbadge-item">
          <div class="missingbadge-icon">
            {{ getBadgeIcon(missbadge.tipo) }}
          </div>
          <div class="badge-info">
            <h4>{{ missbadge.visibleName }}</h4>
            <p class="badge-level">Livello: {{ missbadge.livello }}</p>
            <p class="badge-type">{{ missbadge.target}}</p>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.user-profile {
  max-width: 800px;
  margin: 0 auto;
  padding: 20px;
}

.user-name {
  color: #2c3e50;
  margin-bottom: 20px;
}

.info-section {
  margin-bottom: 20px;
}

.info-list {
  list-style-type: none;
  padding-left: 20px;
}

.info-list li {
  margin: 5px 0;
}

.ratings {
  background-color: transparent;
  padding: 20px;
  border-radius: 8px;
  margin: 20px 0;
  border: 1px solid white;
}

.rating-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin: 10px 0;
}

.rating-label {
  font-weight: bold;
  min-width: 120px;
}

.rating-value {
  display: flex;
  align-items: center;
  gap: 10px;
}

.stars {
  color: #ffd700;
  font-family: monospace;
}

.numeric-rating {
  color: white;
  font-size: 0.9em;
}

.comment-box {
  padding: 15px;
  border: 1px solid white;
  margin: 10px 0;
  font-style: italic;
}

.no-evaluations {
  text-align: center;
  color: #666;
  padding: 20px;
  background-color: white;
  border-radius: 8px;
}

.badges-section {
  margin-top: 30px;
}

.badges-container {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(250px, 1fr));
  gap: 20px;
  margin-top: 15px;
}

.badge-item {
  display: flex;
  align-items: center;
  gap: 15px;
  background-color: #f8f9fa;
  padding: 15px;
  border-radius: 8px;
  transition: transform 0.2s;
}

.missingbadge-item{
  display: flex;
  align-items: center;
  gap: 15px;
  background-color: rgb(204, 203, 203);
  padding: 15px;
  border-radius: 8px;
  transition: transform 0.2s;
}

.badge-item:hover {
  transform: translateY(-2px);
}

.badge-icon {
  width: 50px;
  height: 50px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 24px;
}

.badge-info {
  flex: 1;
}

.badge-info h4 {
  margin: 0 0 5px 0;
  color: #2c3e50;
}

.badge-level {
  margin: 0;
  font-size: 0.9em;
  color: #666;
}

.badge-type {
  margin: 5px 0 0 0;
  font-size: 0.8em;
  color: #888;
}

.badge-teaching {
  background-color: #e3f2fd;
}

.badge-learning {
  background-color: #f3e5f5;
}

.badge-social {
  background-color: #e8f5e9;
}

.badge-achievement {
  background-color: #fff3e0;
}

.missingbadge-icon{
  background-color: rgb(204, 203, 203);
}

.no-badges {
  text-align: center;
  color: #666;
  padding: 20px;
  background-color: #f8f9fa;
  border-radius: 8px;
}

.d-flex {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 20px;
}

.input-form {
  width: 100%;
  max-width: 400px;
  margin-bottom: 20px;
  text-align: center;
}

.checkbox-container {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 20px;
}

.checkbox-container button {
  padding: 5px 20px;
  border: 1px solid #ccc;
  border-radius: 10px;
  background-color: #f9f9f9;
  cursor: pointer;
  transition: background-color 0.3s, color 0.3s;
  font-size: 16px;
}

.checkbox-container button.active {
  background-color: #007bff;
  color: white;
  border-color: #0056b3;
}

.checkbox-container button:hover {
  background-color: #e0e0e0;
}

.checkbox-row {
  display: flex;
  align-items: center;
  margin-bottom: 10px;
}

.checkbox-row input[type="checkbox"] {
  margin-right: 10px;
}

.trend-choose {
  display: flex;
  flex-direction: column;
  gap: 10px;
  margin-top: 20px;
}

canvas#myChart {
  width: 70%;
  height: 100px;
  margin-top: 20px;
}

/* New styles for role management */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
}

.modal-content {
  background-color: white;
  padding: 2rem;
  border-radius: 8px;
  max-width: 500px;
  width: 90%;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
}

.role-change-section {
  margin-bottom: 20px;
  text-align: right;
}

.change-role-btn {
  background-color: #3498db;
  color: white;
  padding: 8px 16px;
  border-radius: 6px;
  font-weight: 500;
  transition: all 0.3s ease;
}

.change-role-btn:hover {
  background-color: #2980b9;
  transform: translateY(-1px);
}

.success-message {
  color: #4CAF50;
  text-align: center;
  padding: 10px;
  margin-top: 10px;
}

.error-message {
  color: #f44336;
  text-align: center;
  padding: 10px;
  margin-top: 10px;
}

.button-group {
  display: flex;
  justify-content: center;
  gap: 10px;
  margin-top: 20px;
}

.cancel-btn {
  padding: 12px 32px;
  border-radius: 8px;
  font-weight: 600;
  background-color: #e2e8f0;
  color: #64748b;
  transition: all 0.3s ease;
}

.cancel-btn:hover {
  background-color: #cbd5e1;
}

.cancel-btn:hover {
  background-color: #cbd5e1;
}

.role-btn {
  padding: 12px 32px;
  border-radius: 8px;
  font-weight: 600;
  background-color: #e2e8f0;
  color: #64748b;
  transition: all 0.3s ease;
  border: 2px solid transparent;
}

.role-btn-active {
  background-color: #3498db;
  color: white;
  border-color: #2980b9;
}

.role-btn:hover {
  background-color: #cbd5e1;
}

.buddy-btn {
  padding: 8px 24px;
  border-radius: 6px;
  font-weight: 500;
  background-color: #f1f5f9;
  color: #64748b;
  transition: all 0.3s ease;
  border: 2px solid transparent;
}

.buddy-btn-active {
  background-color: #3498db;
  color: white;
  border-color: #2980b9;
}

.buddy-btn:hover {
  background-color: #e2e8f0;
}

.confirm-btn {
  padding: 12px 32px;
  border-radius: 8px;
  font-weight: 600;
  background-color: #3498db;
  color: white;
  transition: all 0.3s ease;
}

.confirm-btn:hover:not(.confirm-btn-disabled) {
  background-color: #2980b9;
}

.confirm-btn-disabled {
  opacity: 0.5;
  cursor: not-allowed;
}
</style>